import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { History } from "lucide-react";

const actionTone = { create: "bg-success/15 text-success", update: "bg-accent/20 text-accent-foreground", delete: "bg-destructive/15 text-destructive" };

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AuditLog() {
  const { isAdmin } = useAuth();
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit", entity, action],
    queryFn: async () => (await api.get("/audit", { params: { entity, action } })).data,
    enabled: isAdmin,
  });

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div>
      <PageTitle title="Audit Log" subtitle="Who created, edited or deleted bills and payments" />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="mt-1" data-testid="audit-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Records</SelectItem>
                <SelectItem value="purchase">Purchases</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Action</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="mt-1" data-testid="audit-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Created</SelectItem>
                <SelectItem value="update">Edited</SelectItem>
                <SelectItem value="delete">Deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-3 border-b bg-muted/30 font-semibold text-sm flex items-center gap-2">
          <History className="h-4 w-4 text-primary" /> {logs.length} entries
        </div>
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>When</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead>
                <TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No activity recorded yet.</TableCell></TableRow>
              ) : logs.map((l) => (
                <TableRow key={l.id} data-testid="audit-row">
                  <TableCell className="whitespace-nowrap text-sm">{fmtWhen(l.at)}</TableCell>
                  <TableCell className="text-sm">{l.user_name || l.user_email}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${actionTone[l.action] || "bg-muted"}`}>{l.action}</span></TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{l.entity}</Badge></TableCell>
                  <TableCell className="font-semibold text-primary">{l.ref || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.summary || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
